import { NEW_HOURS, TREND_BADGE, TREND_MIN_V7, TREND_SMOOTH_K, UPDATED_HOURS } from './config';
import type { Entry, HostAgg } from './types';

/** Smoothed week-over-week growth ratio. */
export function trendRatio(v7: number, v7p: number): number {
  return (v7 + TREND_SMOOTH_K) / (v7p + TREND_SMOOTH_K);
}

/** Whether an entry qualifies for the 🔥 badge. */
export function isTrending(agg: HostAgg | null | undefined): boolean {
  if (!agg) return false;
  return agg.v7 >= TREND_MIN_V7 && trendRatio(agg.v7, agg.v7p) >= TREND_BADGE;
}

/** Whether an entry gets the ✨ NEW badge: created within NEW_HOURS of now.
 *
 *  The precise signal is the repo's `createdAt` (a stable UTC timestamp), giving
 *  a true rolling 24h window that drops off exactly NEW_HOURS after creation and
 *  never re-lights when the repo is pushed again. Until that joins from the API
 *  (or for entries with no repo) fall back to the ship date at midnight UTC —
 *  day precision, so a same-day ship still reads NEW on first paint. A creation
 *  or ship time ahead of the viewer's clock (the factory runs in another
 *  timezone, so today's build can read as "tomorrow") counts as brand-new. */
export function isNew(entry: Entry, nowMs: number): boolean {
  const created = entry.createdAt ? Date.parse(entry.createdAt) : Date.parse(`${entry.date}T00:00:00Z`);
  if (Number.isNaN(created)) return false;
  const hoursAgo = (nowMs - created) / 3_600_000;
  return hoursAgo < NEW_HOURS; // negative (clock/timezone skew) still counts as new
}

/** Whether an entry's repo was pushed within UPDATED_HOURS of now.
 *  Callers gate this behind isNew so the NEW badge takes precedence. */
export function isUpdated(entry: Entry, nowMs: number): boolean {
  if (!entry.pushedAt) return false;
  const pushed = Date.parse(entry.pushedAt);
  if (Number.isNaN(pushed)) return false;
  const hoursAgo = (nowMs - pushed) / 3_600_000;
  return hoursAgo < UPDATED_HOURS;
}

/** Parsed push time in ms, or -Infinity when the entry has no push time yet
 *  (unfetched, repo-less, or malformed) so those entries sort last within a date
 *  group and, when neither side has one, fall through to the name tiebreak. */
function pushedMs(e: Entry): number {
  const t = e.pushedAt ? Date.parse(e.pushedAt) : NaN;
  return Number.isNaN(t) ? -Infinity : t;
}

/** Newest first. Ship date (YYYY-MM-DD, day precision) is the primary key, but
 *  the factory can future-date a ship by a timezone, so a single day routinely
 *  holds several ships. Break those same-day ties by the repo's last-push
 *  timestamp, newest first — the only sub-day recency signal we have — then by
 *  name for total stability. pushedAt is joined asynchronously from the GitHub
 *  API, so until it lands every tie falls through to name (the prior behaviour)
 *  and the list re-sorts once the push times arrive. */
export function byNew(a: Entry, b: Entry): number {
  return (
    b.date.localeCompare(a.date) || pushedMs(b) - pushedMs(a) || a.name.localeCompare(b.name)
  );
}

export function byName(a: Entry, b: Entry): number {
  return a.name.localeCompare(b.name);
}

export function byPopular(a: Entry, b: Entry): number {
  const av = a.stats?.agg.v30 ?? -1;
  const bv = b.stats?.agg.v30 ?? -1;
  if (bv !== av) return bv - av;
  const aall = a.stats?.agg.vAll ?? -1;
  const ball = b.stats?.agg.vAll ?? -1;
  if (ball !== aall) return ball - aall;
  return byNew(a, b);
}

export function byTrend(a: Entry, b: Entry): number {
  const at = isTrending(a.stats?.agg);
  const bt = isTrending(b.stats?.agg);
  if (at !== bt) return at ? -1 : 1;
  if (at && bt) {
    const ar = trendRatio(a.stats!.agg.v7, a.stats!.agg.v7p);
    const br = trendRatio(b.stats!.agg.v7, b.stats!.agg.v7p);
    if (br !== ar) return br - ar;
  }
  const av = a.stats?.agg.v7 ?? -1;
  const bv = b.stats?.agg.v7 ?? -1;
  if (bv !== av) return bv - av;
  return byNew(a, b);
}

export function sorterFor(sort: 'new' | 'trending' | 'popular' | 'az'): (a: Entry, b: Entry) => number {
  switch (sort) {
    case 'trending':
      return byTrend;
    case 'popular':
      return byPopular;
    case 'az':
      return byName;
    default:
      return byNew;
  }
}
